import { Injectable, inject, signal } from '@angular/core';
import { PushPlatform } from './push-platform';

/** Chromium's install event; not in the DOM typings yet. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'bn-install-hint-dismissed';
const MOBILE_QUERY = '(pointer: coarse) and (max-width: 767px)';

/**
 * Decides whether to suggest installing Bildigsnetz as a web app (push on the
 * phone) and keeps Chromium's install prompt for the click. Started at boot,
 * because the browser fires `beforeinstallprompt` only once, early.
 */
@Injectable({ providedIn: 'root' })
export class InstallPromptService {
  private readonly platform = inject(PushPlatform);
  private deferred?: BeforeInstallPromptEvent;

  private readonly _visible = signal(false);
  readonly visible = this._visible.asReadonly();

  start(): void {
    this._visible.set(this.shouldSuggest());
    if (typeof window === 'undefined') return;
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();
      this.deferred = event as BeforeInstallPromptEvent;
    });
    window.addEventListener('appinstalled', () => this._visible.set(false));
  }

  /** iPhone/iPad have no install prompt; the hint then shows the steps. */
  isIos(): boolean {
    return this.platform.isIos();
  }

  /** True when the browser's own install dialog can be opened. */
  canPrompt(): boolean {
    return !!this.deferred;
  }

  /** Opens the browser's install dialog; resolves true when installed. */
  async prompt(): Promise<boolean> {
    const event = this.deferred;
    if (!event) return false;
    this.deferred = undefined;
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === 'accepted') this._visible.set(false);
    return outcome === 'accepted';
  }

  dismiss(): void {
    this._visible.set(false);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Without storage the hint comes back on the next visit.
    }
  }

  private shouldSuggest(): boolean {
    if (typeof window === 'undefined') return false;
    if (this.platform.isStandalone()) return false;
    if (!this.platform.isPushSupported() && !this.platform.isIos()) {
      return false;
    }
    if (!window.matchMedia?.(MOBILE_QUERY).matches) return false;
    try {
      return localStorage.getItem(DISMISSED_KEY) !== '1';
    } catch {
      return true;
    }
  }
}
